// Extraído de CalendarioRutas.jsx (refactor mecánico, sin cambios funcionales)
import { MapPin, Users } from "lucide-react";

export const ESTADO_COLOR = {
  planificada: "bg-amber-500/30 text-amber-100 border-amber-500/50",
  en_curso:    "bg-blue-500/30 text-blue-100 border-blue-500/50",
  finalizada:  "bg-emerald-500/30 text-emerald-100 border-emerald-500/50",
  cancelada:   "bg-zinc-700/40 text-zinc-400 border-zinc-700",
};

// Iter 112 · Colores por provincia para el calendario de montajes (línea).
// Basado en la preferencia histórica del usuario en Google Calendar.
export const PROVINCIA_COLOR = {
  "cádiz":    "bg-amber-400/30 text-amber-100 border-amber-400/60",
  "cadiz":    "bg-amber-400/30 text-amber-100 border-amber-400/60",
  "sevilla":  "bg-lime-500/25 text-lime-100 border-lime-500/50",
  "huelva":   "bg-sky-500/25 text-sky-100 border-sky-500/50",
  "málaga":   "bg-purple-500/25 text-purple-100 border-purple-500/50",
  "malaga":   "bg-purple-500/25 text-purple-100 border-purple-500/50",
  "portugal": "bg-orange-500/25 text-orange-100 border-orange-500/50",
  "córdoba":  "bg-rose-500/25 text-rose-100 border-rose-500/50",
  "cordoba":  "bg-rose-500/25 text-rose-100 border-rose-500/50",
  "granada":  "bg-fuchsia-500/25 text-fuchsia-100 border-fuchsia-500/50",
  "almería":  "bg-teal-500/25 text-teal-100 border-teal-500/50",
  "almeria":  "bg-teal-500/25 text-teal-100 border-teal-500/50",
  "jaén":     "bg-yellow-600/30 text-yellow-100 border-yellow-600/50",
  "jaen":     "bg-yellow-600/30 text-yellow-100 border-yellow-600/50",
};
export const PROVINCIA_DEFAULT_COLOR = "bg-zinc-700/40 text-zinc-200 border-zinc-600";
export const provinciaColor = (prov) => {
  if (!prov) return PROVINCIA_DEFAULT_COLOR;
  return PROVINCIA_COLOR[prov.toLowerCase().trim()] || PROVINCIA_DEFAULT_COLOR;
};

export function RutaPill({ ruta, onClick, onDragStart, compact }) {
  const color = ESTADO_COLOR[ruta.estado] || ESTADO_COLOR.planificada;
  const paradas = (ruta.paradas || []).length;
  // Iter 58 — contador de componentes a montar (suma de componentes_incluidos por parada)
  const totalComps = (ruta.paradas || []).reduce(
    (acc, p) => acc + (p.componentes_incluidos?.length || 0), 0
  );
  const montadoresNombres = (ruta.montadores || []).map(m => m.nombre_visible).slice(0, 2).join(", ");
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`cal-ruta-pill-${ruta.id}`}
      className={`cursor-pointer rounded border ${color} hover:brightness-125 transition-all px-1.5 py-0.5 group`}>
      {compact ? (
        <div className="flex items-center gap-1 text-[10px]">
          {ruta.hora_salida && <span className="opacity-70">{ruta.hora_salida}</span>}
          <span className="truncate font-medium">{ruta.nombre || ruta.codigo}</span>
        </div>
      ) : (
        <>
          <div className="text-[10px] font-mono opacity-70">{ruta.codigo}</div>
          <div className="text-xs font-semibold truncate">{ruta.nombre}</div>
          <div className="text-[10px] opacity-80 mt-0.5 flex items-center gap-1 flex-wrap">
            {ruta.hora_salida && <span>🕗 {ruta.hora_salida}</span>}
            <span><MapPin className="w-2.5 h-2.5 inline" />{paradas}</span>
            {totalComps > 0 && (
              <span className="text-emerald-300 font-mono" title={`${totalComps} componente(s) a montar`}>
                📦{totalComps}
              </span>
            )}
            {montadoresNombres && (
              <span className="flex items-center gap-0.5">
                <Users className="w-2.5 h-2.5" />{montadoresNombres}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// Iter 112 · Pill de montaje (línea) para el calendario — coloreado por provincia
export function MontajePill({ montaje, onClick, compact }) {
  const prov = montaje.espacio?.provincia || "";
  const cls = provinciaColor(prov);
  const cli = montaje.cliente_nombre || "—";
  const espNombre = montaje.espacio?.nombre || "";
  const marca = montaje.peticion?.marca || "";
  const nComps = montaje.num_componentes || 0;
  const tipos = (montaje.tipos && montaje.tipos.length) ? montaje.tipos.join(" · ") : (montaje.tipo || "");
  const enRuta = !!montaje.agendada_en_ruta_id;
  const montadoresNombres = (montaje.montadores || []).map(m => m.nombre_visible).slice(0, 2).join(", ");
  const tooltip = [
    `${montaje.peticion?.codigo || ""} · ${montaje.peticion?.nombre || ""}`,
    `Cliente: ${cli}`,
    marca ? `Marca: ${marca}` : null,
    espNombre ? `Espacio: ${espNombre}` : null,
    prov ? `Provincia: ${prov}` : null,
    tipos ? `Tipo: ${tipos}` : null,
    nComps ? `${nComps} componente(s)` : null,
    enRuta ? "En ruta · agendada" : "Sin agendar",
  ].filter(Boolean).join("\n");
  return (
    <div
      onClick={onClick}
      title={tooltip}
      data-testid={`cal-montaje-pill-${montaje.id}`}
      className={`cursor-pointer rounded border ${cls} hover:brightness-125 transition-all px-1.5 py-0.5`}>
      {compact ? (
        <div className="flex items-center gap-1 text-[10px]">
          <span className="truncate font-medium">{espNombre || cli}</span>
          {enRuta && <span className="text-[9px] opacity-70">🚚</span>}
        </div>
      ) : (
        <>
          <div className="text-[10px] font-mono opacity-70 flex items-center gap-1 justify-between">
            <span className="truncate">{montaje.peticion?.codigo || ""}</span>
            {enRuta && <span title="Ya en ruta">🚚</span>}
          </div>
          <div className="text-xs font-semibold truncate">{espNombre || cli}</div>
          <div className="text-[10px] opacity-80 mt-0.5 flex items-center gap-1 flex-wrap">
            {marca && <span className="truncate max-w-[70px]">🏷 {marca}</span>}
            {nComps > 0 && (
              <span className="font-mono" title={`${nComps} componente(s)`}>📦{nComps}</span>
            )}
            {montadoresNombres && (
              <span className="flex items-center gap-0.5">
                <Users className="w-2.5 h-2.5" />{montadoresNombres}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
